import type { PriceTagDraft } from '@recipes/contracts';
import { checkPricePlausibility, parseMoneyInput } from '@recipes/domain';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@recipes/ui';
import { Camera } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/feedback/toaster';
import { savePrice } from '@/features/catalog/catalog-queue';
import { useCatalogStores } from '@/features/catalog/hooks';
import { storeLabel } from '@/features/catalog/store-label';
import { useAiConsent, useFeature } from '@/features/shared/hooks';
import { ProcessingIndicator } from '@/features/shared/processing-indicator';
import { describeWorkerError, toastError } from '@/lib/errors';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import {
  formatMoney,
  priceHere,
  storeRefOf,
  today,
  useCurrency,
  usePriceRules,
  usePricesFor,
} from './hooks';
import { TripPhotoQueueFullError, startPriceTagScan } from './trip-photos';
import { useTagScan } from './use-tag-scan';

const NO_STORE = '__none__';

export interface PriceTarget {
  productId?: string | null;
  ingredientId?: string | null;
  name: string;
  packageQuantity?: string | null;
  packageUnitCode?: string | null;
}

/**
 * Record a price in a few seconds at the shelf (docs/partial/shopping-in-store-spec.md 5.3):
 * the store is prefilled from "Shopping at …", "1,19" and "1.19" both work, and saving goes to
 * the device first. A photo of the price tag prefills the form – it never saves by itself.
 */
export function PriceDialog({
  open,
  onOpenChange,
  target,
  defaultStoreId,
  scan,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: PriceTarget;
  defaultStoreId?: string | null;
  /** A price tag scan from the inbox that this dialog confirms. */
  scan?: { id: string; draft: PriceTagDraft } | null;
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const ids = { price: useId(), store: useId(), promo: useId(), until: useId(), deposit: useId() };
  const stores = useCatalogStores();
  const currency = useCurrency();
  const rules = usePriceRules();
  const online = useOnlineStatus();
  const tagScanFeature = useFeature('price_tag_scan');
  const aiFeature = useFeature('ai');
  const consented = useAiConsent();
  const known = usePricesFor(target);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const [priceText, setPriceText] = useState('');
  const [storeId, setStoreId] = useState<string>(NO_STORE);
  const [promotion, setPromotion] = useState(false);
  const [promotionEndsAt, setPromotionEndsAt] = useState('');
  const [depositText, setDepositText] = useState('');
  const [perKilo, setPerKilo] = useState(false);
  const [asked, setAsked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [scanId, setScanId] = useState<string | null>(null);
  const [applied, setApplied] = useState<string | null>(null);
  const liveScan = useTagScan(scanId);

  const applyDraft = (draft: PriceTagDraft) => {
    setPriceText(draft.price.replace('.', ','));
    setPromotion(draft.isPromotion);
    setPromotionEndsAt(draft.promotionEndsAt ?? '');
  };

  useEffect(() => {
    if (!open) return;
    setPriceText('');
    setStoreId(defaultStoreId ?? NO_STORE);
    setPromotion(false);
    setPromotionEndsAt('');
    setDepositText('');
    // Loose goods are priced by weight; a product by its package.
    setPerKilo(!target.productId);
    setAsked(false);
    setScanId(scan?.id ?? null);
    setApplied(scan?.id ?? null);
    if (scan) applyDraft(scan.draft);
    // Only when the dialog opens – typing must not be reset by a re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The photo was read: prefill, once.
  useEffect(() => {
    if (liveScan?.status !== 'READY' || !liveScan.draft || applied === liveScan.id) return;
    setApplied(liveScan.id);
    applyDraft(liveScan.draft);
  }, [applied, liveScan]);

  const price = parseMoneyInput(priceText);
  const deposit = depositText.trim() ? parseMoneyInput(depositText) : null;
  const store = stores.find((candidate) => candidate.id === storeId) ?? null;
  const previous = useMemo(
    () => (store ? priceHere(known, storeRefOf(store), currency, rules) : null),
    [currency, known, rules, store],
  );
  const plausibility = price
    ? checkPricePlausibility(price, previous?.current.observation.price)
    : 'OK';
  // The classic missing comma: the first Save asks, the second one saves.
  const implausible = plausibility !== 'OK';
  const draft = liveScan?.status === 'READY' ? liveScan.draft : (scan?.draft ?? null);
  const draftCurrency = draft?.currency ?? currency;

  const scanning = uploading || liveScan?.status === 'PROCESSING';
  const canScan = tagScanFeature && aiFeature;
  const scanBlocked = !consented
    ? t('prices.tag.needsAi')
    : !online
      ? t('prices.tag.offlineQueued')
      : null;

  const onPhoto = async (files: FileList | null) => {
    const file = files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const started = await startPriceTagScan({
        file,
        productId: target.productId ?? null,
        storeId: store?.id ?? null,
      });
      if (started.queued) toast.show({ title: t('prices.tag.queued') });
      else setScanId(started.result.id);
    } catch (error) {
      if (error instanceof TripPhotoQueueFullError) toast.error(t('prices.tag.queueFull'));
      else toastError(error);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!price || (depositText.trim() && !deposit)) return;
    if (implausible && !asked) {
      setAsked(true);
      return;
    }
    setSaving(true);
    try {
      await savePrice({
        productId: target.productId ?? null,
        ingredientId: target.productId ? null : (target.ingredientId ?? null),
        storeId: store?.id ?? null,
        chainId: store ? (store.parentStoreId ?? store.id) : null,
        price,
        currency: draftCurrency,
        perQuantity: perKilo ? '1' : null,
        perUnitCode: perKilo ? 'kg' : null,
        packageQuantity: target.packageQuantity ?? null,
        packageUnitCode: target.packageUnitCode ?? null,
        isPromotion: promotion,
        promotionEndsAt: promotion && promotionEndsAt ? promotionEndsAt : null,
        depositAmount: deposit,
        observedAt: today(),
        ...(liveScan?.status === 'READY' || scan ? { tagScanId: (liveScan ?? scan)!.id } : {}),
      });
      toast.success(t('prices.saved', { price: formatMoney(price, draftCurrency) }));
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')} data-testid="price-dialog">
        <DialogHeader>
          <DialogTitle>{t('prices.dialog.title')}</DialogTitle>
          <DialogDescription>{target.name}</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <div>
            <Label htmlFor={ids.price}>
              {perKilo ? t('prices.dialog.pricePerKilo') : t('prices.dialog.price')}
            </Label>
            <div className="mt-1 flex items-center gap-2">
              <Input
                id={ids.price}
                autoFocus
                inputMode="decimal"
                autoComplete="off"
                enterKeyHint="done"
                placeholder="1,19"
                value={priceText}
                onChange={(event) => {
                  setPriceText(event.target.value);
                  setAsked(false);
                }}
                aria-invalid={priceText.trim() !== '' && !price}
                className="h-12 text-lg"
                data-testid="price-input"
              />
              <span className="text-sm text-muted-foreground">{draftCurrency}</span>
            </div>
            {priceText.trim() !== '' && !price && (
              <p className="mt-1 text-xs text-destructive" role="alert">
                {t('prices.dialog.invalid')}
              </p>
            )}
            {previous && (
              <p className="mt-1 text-xs text-muted-foreground" data-testid="price-previous">
                {t('prices.dialog.previous', {
                  price: formatMoney(previous.current.observation.price, currency),
                })}
              </p>
            )}
          </div>

          {implausible && asked && price && (
            <Alert variant="warning" data-testid="price-implausible">
              {t('prices.dialog.really', { price: formatMoney(price, draftCurrency) })}
            </Alert>
          )}

          <div>
            <Label htmlFor={ids.store}>{t('prices.dialog.store')}</Label>
            <Select value={storeId} onValueChange={setStoreId}>
              <SelectTrigger id={ids.store} className="mt-1" data-testid="price-store">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_STORE}>{t('prices.dialog.somewhere')}</SelectItem>
                {stores.map((candidate) => (
                  <SelectItem key={candidate.id} value={candidate.id}>
                    {storeLabel(candidate)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {storeId === NO_STORE && (
              <p className="mt-1 text-xs text-muted-foreground">{t('prices.dialog.noStoreHint')}</p>
            )}
          </div>

          {!target.productId && (
            <div className="flex items-center gap-3">
              <Switch checked={perKilo} onCheckedChange={setPerKilo} id={`${ids.price}-kg`} />
              <Label htmlFor={`${ids.price}-kg`}>{t('prices.dialog.perKilo')}</Label>
            </div>
          )}

          <div className="flex items-start gap-3">
            <Switch
              id={ids.promo}
              checked={promotion}
              onCheckedChange={setPromotion}
              data-testid="price-promotion"
            />
            <div className="flex-1">
              <Label htmlFor={ids.promo}>{t('prices.dialog.promotion')}</Label>
              {promotion && (
                <div className="mt-2">
                  <Label htmlFor={ids.until} className="text-xs">
                    {t('prices.dialog.promotionUntil')}
                  </Label>
                  <Input
                    id={ids.until}
                    type="date"
                    min={today()}
                    value={promotionEndsAt}
                    onChange={(event) => setPromotionEndsAt(event.target.value)}
                    className="mt-1 max-w-[12rem]"
                  />
                </div>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor={ids.deposit}>{t('prices.dialog.deposit')}</Label>
            <Input
              id={ids.deposit}
              inputMode="decimal"
              autoComplete="off"
              placeholder="0,25"
              value={depositText}
              onChange={(event) => setDepositText(event.target.value)}
              aria-invalid={depositText.trim() !== '' && !deposit}
              className="mt-1 max-w-[8rem]"
            />
          </div>

          {canScan && (
            <div className="space-y-2 border-t pt-3">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="sr-only"
                tabIndex={-1}
                aria-hidden
                onChange={(event) => void onPhoto(event.target.files)}
                data-testid="price-tag-input"
              />
              <Button
                type="button"
                variant="outline"
                disabled={!consented || scanning}
                onClick={() => fileRef.current?.click()}
                data-testid="price-tag-scan"
              >
                <Camera aria-hidden /> {t('prices.tag.scan')}
              </Button>
              {scanBlocked && <p className="text-xs text-muted-foreground">{scanBlocked}</p>}
              {scanning && <ProcessingIndicator compact label={t('prices.tag.reading')} />}
              {liveScan?.status === 'FAILED' && (
                <Alert variant="destructive" data-testid="price-tag-failed">
                  {describeWorkerError(liveScan.errorCode)}
                </Alert>
              )}
              {draft && (
                <div className="rounded-md bg-muted/40 p-2 text-xs" data-testid="price-tag-draft">
                  <p className="font-medium">{t('prices.tag.read')}</p>
                  <p>
                    {[draft.brand, draft.productName].filter(Boolean).join(' ')}
                    {draft.packageQuantity &&
                      ` · ${draft.packageQuantity} ${draft.packageUnitCode ?? draft.packageUnitText ?? ''}`}
                    {draft.unitPrice &&
                      ` · ${t('prices.unitPrice', {
                        price: formatMoney(draft.unitPrice, draftCurrency),
                        base: t(`prices.base.${draft.unitPriceBase ?? 'KG'}`),
                      })}`}
                  </p>
                  {draft.unitPriceMismatch && (
                    <p className="text-destructive">{t('prices.tag.unitPriceMismatch')}</p>
                  )}
                  {draft.confidence === 'LOW' && <p>{t('prices.tag.lowConfidence')}</p>}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={saving}
              disabled={!price || (depositText.trim() !== '' && !deposit)}
              data-testid="price-save"
            >
              {implausible && asked ? t('prices.dialog.saveAnyway') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
